import {
  Stack,
  Title,
  Text,
  Group,
  Paper,
  ActionIcon,
  Select,
  TextInput,
  Button,
} from "@mantine/core";
import { Dropzone, MS_EXCEL_MIME_TYPE } from "@mantine/dropzone";
import { IconFileSpreadsheet, IconTrash } from "@tabler/icons-react";

export function ManifestStep({
  manifestFile,
  onManifestDrop,
  onRemoveManifest,
  identifierOptions,
  manifestHeaders,
  identifierColumn,
  onIdentifierColumnChange,
  filenameColumn,
  onFilenameColumnChange,
  documentName,
  onDocumentNameChange,
  onValidate,
  isPending,
  errors,
}) {
  return (
    <Stack gap="md">
      <Title order={3}>Upload Manifest</Title>
      <Text size="sm" c="dimmed">
        Upload a CSV file containing document mapping information.
      </Text>

      <TextInput
        label="Document Name"
        placeholder="e.g., Certificate"
        value={documentName}
        onChange={(e) => onDocumentNameChange(e.currentTarget.value)}
        error={errors.documentName?.message}
        withAsterisk={true}
        description="Name used to label and tag uploaded documents"
      />

      {!manifestFile ? (
        <Dropzone
          onDrop={onManifestDrop}
          maxFiles={1}
          maxSize={5 * 1024 * 1024} // 5MB limit
          accept={[...MS_EXCEL_MIME_TYPE, "text/csv"]}
        >
          <Group
            justify="center"
            gap="xl"
            mih={180}
            style={{ pointerEvents: "none" }}
          >
            <IconFileSpreadsheet size="3rem" stroke={1.5} />
            <div>
              <Text size="xl" inline>
                Drag manifest here or click to select
              </Text>
              <Text size="sm" c="dimmed" mt={7}>
                CSV file, less than 5MB
              </Text>
            </div>
          </Group>
        </Dropzone>
      ) : (
        <Paper withBorder p="md" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between">
            <Group gap="md">
              <IconFileSpreadsheet size="1.5rem" color="green" />
              <div>
                <Text fw={500}>{manifestFile.name}</Text>
                <Text size="xs" c="dimmed">
                  {(manifestFile.size / 1024).toFixed(1)} KB
                </Text>
              </div>
            </Group>
            <ActionIcon
              color="red"
              variant="subtle"
              size="sm"
              onClick={onRemoveManifest}
            >
              <IconTrash size="1rem" />
            </ActionIcon>
          </Group>
        </Paper>
      )}

      <Group grow align="flex-start">
        <Select
          label="User Identifier Column"
          placeholder="e.g., Email or Ticket ID"
          data={identifierOptions}
          value={identifierColumn}
          onChange={onIdentifierColumnChange}
          searchable
          clearable
          allowDeselect={false}
          error={errors.identifierColumn?.message}
          withAsterisk={true}
          description="Match with system identifier"
        />
        <Select
          label="Filename Column"
          placeholder="e.g., Certificate_Name"
          data={manifestHeaders}
          value={filenameColumn}
          onChange={onFilenameColumnChange}
          searchable
          clearable
          error={errors.filenameColumn?.message}
          withAsterisk={true}
          description="Column with file names (e.g. MyDocument.pdf)"
        />
      </Group>

      <Group justify="flex-end" mt="xl">
        <Button
          onClick={onValidate}
          disabled={
            !manifestFile ||
            !identifierColumn ||
            !filenameColumn ||
            !documentName
          }
          loading={isPending}
        >
          Validate Manifest
        </Button>
      </Group>
    </Stack>
  );
}
